import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { useFlagIdentity } from "./posthog.context";

interface User {
  id: string;
  email: string;
  name: string;
}

interface AuthContext {
  user: User | null;
  isLoading: boolean;
  login: () => void;
  logout: () => Promise<void>;
}

const authContext = createContext<AuthContext | null>(null);

export function useAuth() {
  const ctx = useContext(authContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const flagIdentity = useFlagIdentity();

  const fetchMe = useCallback(async () => {
    try {
      const res = await fetch("/api/auth/me", { credentials: "include" });
      if (res.status === 401) {
        setUser(null);
        // Unauthenticated → backend handles the sign-in flow. Don't redirect
        // when the user is intentionally on the signed-out landing page.
        if (window.location.pathname !== "/login") {
          window.location.href = "/api/auth/login";
        }
        return;
      }
      if (!res.ok) {
        console.error(`/api/auth/me returned ${res.status}`);
        setUser(null);
        return;
      }
      const me = await res.json();
      setUser(me);
      flagIdentity.identify(me.id, me.email);
    } catch (err) {
      console.error("/api/auth/me fetch failed:", err);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(() => {
    // Go backend handles the sign-in flow: WorkOS in prod, dev picker in local.
    window.location.href = "/api/auth/login";
  }, []);

  const logout = useCallback(async () => {
    let workosLogoutURL: string | undefined;
    try {
      const res = await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
      if (res.ok) {
        const data = (await res.json()) as { workosLogoutURL?: string };
        workosLogoutURL = data.workosLogoutURL;
      }
    } catch {
      // Clear local state even if the request fails.
    }
    setUser(null);
    // Prod: navigate to the WorkOS logout URL so the browser SSO session is
    // terminated too — otherwise re-signin would auto-auth via SSO. WorkOS
    // then redirects back (to POST_LOGOUT_REDIRECT_URL if configured, or its
    // dashboard default). Local: no WorkOS roundtrip; land on /login directly.
    window.location.href = workosLogoutURL || "/login";
  }, []);

  return (
    <authContext.Provider value={{ user, isLoading, login, logout }}>
      {children}
    </authContext.Provider>
  );
}
